import { useEffect, useRef, useState } from 'react';

type NumberType = 'integer' | 'decimal';

export const useAnimatedCounter = (
  target: number,
  duration: number = 600,
  isVisible: boolean = false,
  numberType: NumberType = 'decimal',
) => {
  const [count, setCount] = useState(0);
  const requestRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // When not visible, reset to 0
    if (!isVisible) {
      setCount(0);
      return;
    }

    const startValue = count; // Animate from current value
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Smoother easing function for a more natural feel
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);

      const newCount = startValue + (target - startValue) * easeOutCubic;

      // Apply proper rounding based on number type. Decimals are capped to 2
      // places during the animation so intermediate frames don't flash long
      // floats (e.g. 0.8888888884); the final frame below still settles on the
      // exact target, preserving the value's real precision once it lands.
      const roundedCount = numberType === 'integer' ? Math.round(newCount) : Math.round(newCount * 100) / 100;
      setCount(roundedCount);

      if (progress < 1) {
        requestRef.current = requestAnimationFrame(animate);
      } else {
        // Ensure final value is exact and properly typed
        const finalValue = numberType === 'integer' ? Math.round(target) : target;
        setCount(finalValue);
      }
    };

    // Start the animation
    requestRef.current = requestAnimationFrame(animate);

    // Cleanup function to cancel animation frame
    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
    // Rerun animation only when target, visibility, or number type changes
  }, [target, isVisible, duration, numberType]);

  return count;
};
